/**
 * The multi-office checkout, as pure functions: what is in the cart, what it
 * costs, what we remember across Helcim's page round trip, and what to say
 * afterwards. Everything here is testable without React or a network.
 *
 * Money arrives as decimal strings from the catalog, priced for each office
 * (offers applied) — see usage.services.pricing. Totals are summed in cents so
 * three offices never add up to $12,345.669999.
 */

import { sumTaxLines, taxLinesOf } from "./taxLines";

export const PENDING_SUBSCRIBE_KEY = "pendingSubscribe";

const cents = (value) => Math.round(Number(value || 0) * 100);
const dollars = (c) => (c / 100).toFixed(2);

/**
 * Offices the buyer can still choose a plan for: no active plan, and theirs to
 * buy. `preview` (a super admin looking at what the practice sees) lists every
 * office that still needs a plan, whether or not the viewer may pay for it.
 */
export function officesNeedingPlan(practicePlans, { preview = false } = {}) {
  const offices = (practicePlans && practicePlans.offices) || [];
  return offices.filter(
    (o) => (preview || o.can_purchase) && !(o.plan && o.plan.status === "active") && o.state !== "exempt",
  );
}

/** One line per office with a chosen plan. `selections` = {officeId: variant}. */
export function cartLines(offices, selections) {
  return (offices || [])
    .filter((o) => selections && selections[o.office_id])
    .map((o) => {
      const v = selections[o.office_id];
      const c = v.charge || {};
      return {
        officeId: o.office_id,
        officeName: o.office_name,
        tierId: v.id,
        title: v.title,
        isAnnual: !!v.is_annual,
        listSubtotal: c.list_subtotal != null ? String(c.list_subtotal) : String(c.subtotal || "0"),
        discount: String(c.discount || "0"),
        subtotal: String(c.subtotal || "0"),
        tax: String(c.tax || "0"),
        // Each office is taxed at its own rate, so the lines travel per office.
        taxLines: taxLinesOf(c),
        total: String(v.charge_amount != null ? v.charge_amount : c.total || "0"),
        offerLabel: (v.offer && v.offer.label) || (c.offer && c.offer.label) || "",
      };
    });
}

/**
 * Sums over the lines: {listSubtotal, discount, subtotal, tax, total, taxLines}.
 * `taxLines` adds up each tax across the offices (one row per tax and rate), so
 * an Ontario office and a BC office show HST, GST and PST on their own rows.
 */
export function cartTotals(lines) {
  const sum = (key) => dollars((lines || []).reduce((acc, l) => acc + cents(l[key]), 0));
  return {
    listSubtotal: sum("listSubtotal"),
    discount: sum("discount"),
    subtotal: sum("subtotal"),
    tax: sum("tax"),
    total: sum("total"),
    taxLines: sumTaxLines((lines || []).map((l) => l.taxLines)),
  };
}

/** The checkout request body's items. */
export function checkoutItems(lines) {
  return (lines || []).map((l) => ({ office_id: l.officeId, subscription_tier_id: l.tierId }));
}

/**
 * What survives Helcim's full-page round trip. v2 names every office in the
 * checkout; `officeId` (the first) is kept so an older reader still sees a
 * subscription return rather than a patient payment.
 */
export function pendingPurchasePayload({ checkoutRef, officeIds, tierIds }) {
  const ids = (officeIds || []).map(String);
  return JSON.stringify({
    v: 2,
    checkoutRef: checkoutRef || String(Date.now()),
    purchaseId: checkoutRef || String(Date.now()),
    officeIds: ids,
    officeId: ids[0] || null,
    tierIds: (tierIds || []).map(String),
  });
}

/** The office ids a pending purchase covers (v1 or v2), or []. */
export function pendingOfficeIds(raw) {
  try {
    const p = JSON.parse(raw);
    if (!p) return [];
    if (Array.isArray(p.officeIds) && p.officeIds.length) return p.officeIds.map(String);
    return p.officeId ? [String(p.officeId)] : [];
  } catch (e) {
    return [];
  }
}

/** How long to wait for the charges: a charge per office runs one after another. */
export function pollWindowMs(officeCount) {
  return 20000 + 10000 * Math.max(0, (officeCount || 1) - 1);
}

/** One message for a checkout's outcome over all its offices.
 *  `states` = {officeId: state}; `names` = {officeId: name}. */
export function checkoutOutcome(states, names = {}) {
  const ids = Object.keys(states || {});
  const nameOf = (id) => names[id] || `Office ${id}`;
  const by = (pred) => ids.filter((id) => pred(states[id]));
  const active = by((s) => s === "active");
  const declined = by((s) => s === "declined" || s === "provider_error");
  if (ids.length && active.length === ids.length) {
    return {
      kind: "success",
      text: ids.length === 1 ? "Your plan is active. Thank you!" : `Plans are active for all ${ids.length} offices. Thank you!`,
    };
  }
  if (declined.length) {
    return {
      kind: "error",
      text: `The card was declined for ${declined.map(nameOf).join(", ")}.` +
        (active.length ? ` ${active.map(nameOf).join(", ")} ${active.length === 1 ? "is" : "are"} active.` : "") +
        " Please try another card.",
    };
  }
  return {
    kind: "info",
    text: "We're confirming your payment. Please check back in a moment — don't enter the card again.",
  };
}
